import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { EXHUT_WEB_LOCAL_STORAGE_TOKEN } from "../utils/constants";

interface IDeleteNotificationPayload {
  transactionId: string;
}

export function useDeleteNotification() {
  return _useDelete<any, any, IDeleteNotificationPayload>({
    path: `transactions/:transactionId`,
    keys: ["notifications"],
    replacePathParams: "transactionId",
  });
}

interface IDeleteIncomePayload {
  incomeId: string;
}

export function useDeleteIncome(groupId?: string) {
  return _useDelete<any, any, IDeleteIncomePayload>({
    path: `groups/${groupId}/incomes/:incomeId`,
    keys: ["groups", groupId, "incomes"],
    replacePathParams: "incomeId",
  });
}

export function useDeleteMe() {
  return _useDelete({
    path: `users`,
    keys: ["users"],
    host: process.env.REACT_APP_AUTH_BE_URL,
  });
}

export function useDeleteSubscription() {
  return _useDelete<any, any, void>({
    path: `subscriptions`,
    keys: ["subscriptions"],
  });
}

interface IDeleteGroupPayload {
  groupId: string;
}

export function useDeleteGroup2() {
  return _useDelete<any, any, IDeleteGroupPayload>({
    path: `groups/:groupId`,
    keys: ["groups"],
    replacePathParams: "groupId",
  });
}

interface IDeleteLedgerPayload {
  ledgerId: string;
}

export function useDeleteLedger(groupId?: string) {
  return _useDelete<any, any, IDeleteLedgerPayload>({
    path: `ledgers/:ledgerId`,
    keys: ["groups", groupId, "ledgers"],
    replacePathParams: "ledgerId",
  });
}

interface IDeleteAccountPayload {
  accountId: string;
}

export function useDeleteAccount() {
  return _useDelete<any, any, IDeleteAccountPayload>({
    path: `accounts/:accountId`,
    keys: ["accounts"],
    replacePathParams: "accountId",
  });
}

interface IDeleteTripPayload {
  tripId: string;
}

export function useDeleteTrip(groupId?: string) {
  return _useDelete<any, any, IDeleteTripPayload>({
    path: `groups/${groupId}/trips/:tripId`,
    keys: ["groups", groupId, "trips"],
    replacePathParams: "tripId",
  });
}

interface IDeleteTripExpensePayload {
  expenseId: string;
}

export function useDeleteTripExpense(groupId?: string, tripId?: string) {
  return _useDelete<any, any, IDeleteTripExpensePayload>({
    path: `groups/${groupId}/trips/${tripId}/expenses/:expenseId`,
    keys: ["groups", groupId!, "trips", tripId!, "expenses"],
    replacePathParams: "expenseId",
  });
}

interface IDeleteSavingsGoalPayload {
  goalId: string;
}

export function useDeleteSavingsGoal(groupId?: string) {
  return _useDelete<any, any, IDeleteSavingsGoalPayload>({
    path: `groups/${groupId}/savings-goals/:goalId`,
    keys: ["groups", groupId, "savings-goals"],
    replacePathParams: "goalId",
  });
}

interface IDeleteSavingsGoalItemPayload {
  itemId: string;
}

export function useDeleteSavingsGoalItem(groupId?: string, goalId?: string) {
  return _useDelete<any, any, IDeleteSavingsGoalItemPayload>({
    path: `groups/${groupId}/savings-goals/${goalId}/items/:itemId`,
    keys: ["groups", groupId!, "savings-goals", goalId!, "items"],
    replacePathParams: "itemId",
  });
}

interface IDeleteExpensePayload {
  expenseId: string;
}

export function useDeleteExpense(groupId?: string) {
  return _useDelete<any, any, IDeleteExpensePayload>({
    path: `groups/${groupId}/expenses/:expenseId`,
    keys: ["groups", groupId, "expenses"],
    replacePathParams: "expenseId",
  });
}

export function useDeleteLedgerExpense(groupId?: string, ledgerId?: string) {
  return _useDelete<any, any, IDeleteExpensePayload>({
    path: `groups/${groupId}/ledgers/${ledgerId}/transactions/:expenseId`,
    keys: ["groups", groupId, "ledgers", ledgerId, "transactions"],
    replacePathParams: "expenseId",
  });
}

interface IDeleteSavingsPayload {
  savingsId: string;
}

export function useDeleteSavings(groupid?: string) {
  return _useDelete<any, any, IDeleteSavingsPayload>({
    path: `groups/${groupid}/savings/:savingsId`,
    keys: ["groups", groupid!, "savings"],
    replacePathParams: "savingsId",
  });
}

function _useDelete<T1, T2, T3>({
  path,
  keys,
  host,
  replacePathParams,
}: {
  host?: string;
  path: string;
  keys: (string | null | undefined)[];
  replacePathParams?: string;
}) {
  const _host = host ?? process.env.REACT_APP_BE_URL2;
  let url = `${_host}/${path}`;
  return useMutation<T1, T2, T3>({
    mutationFn: (req) => {
      if (replacePathParams) {
        url = url.replace(
          `:${replacePathParams}`,
          (req as any)[replacePathParams],
        );
      }
      const sign = localStorage.getItem(EXHUT_WEB_LOCAL_STORAGE_TOKEN);
      return axios.delete(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: sign,
          Bearer: sign,
        },
      });
    },
    mutationKey: keys,
  });
}
